import type { MentorStoryImage } from "@/lib/api/mentor-stories";
import {
  useMyStoryReview,
  type StoryReview,
  type StoryReviewStatus,
} from "@/lib/api/mentor-story-review";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { Button } from "@somethings-ui/web";
import { Clock, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { FirstMomentsAccordion } from "../first-moments-accordion";
import { StoryEditorDialog } from "./story-editor-dialog";

interface StorySectionProps {
  userId: string;
  publishedImages: MentorStoryImage[];
}

export function StorySection({ userId, publishedImages }: StorySectionProps) {
  const { data: review, isPending } = useMyStoryReview();
  const { track } = useTrackEvent();
  const [expanded, setExpanded] = useState(true);
  const [open, setOpen] = useState(false);

  const openEditor = () => {
    track(PostHogEvents.MENTOR_STORY_EDIT_CTA_CLICKED, { mentor_id: userId });
    setOpen(true);
  };

  if (isPending) {
    return <div className="h-40 animate-pulse rounded-2xl bg-surfaces-z3" />;
  }

  return (
    <FirstMomentsAccordion
      title="Stories"
      expandedHint="The stories that teens tap through after matching to get to know you more."
      collapsedSummary={
        publishedImages.length > 0
          ? `${publishedImages.length} slides`
          : "No published story"
      }
      open={expanded}
      onToggle={() => setExpanded((current) => !current)}
    >
      <div className="flex flex-col gap-lg">
        <PublishedStory images={publishedImages} />
        <StoryEditorCard review={review ?? null} onEdit={openEditor} />
      </div>
      {open ? (
        <StoryEditorDialog
          userId={userId}
          review={review ?? null}
          open
          onOpenChange={setOpen}
        />
      ) : null}
    </FirstMomentsAccordion>
  );
}

export function PublishedStory({ images }: { images: MentorStoryImage[] }) {
  const [preview, setPreview] = useState<MentorStoryImage | null>(null);
  const published = [...images].sort((a, b) => a.order - b.order);
  return (
    <>
      <CurrentStory images={published} onOpen={setPreview} />
      {preview ? (
        <button
          type="button"
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-trueBlack/70"
          onClick={() => setPreview(null)}
        >
          <PublishedImage
            image={preview}
            className="h-[80vh] w-auto max-w-[90vw] rounded-2xl object-contain"
          />
        </button>
      ) : null}
    </>
  );
}

function CurrentStory({
  images,
  onOpen,
}: {
  images: MentorStoryImage[];
  onOpen: (image: MentorStoryImage) => void;
}) {
  return (
    <section className="flex flex-col gap-lg rounded-2xl border border-surfaces-z5 bg-brand-ivory px-xl py-xl">
      <div className="flex items-center gap-md">
        <p className="font-mono text-caption-3 uppercase text-text-tertiary">
          CURRENT STORY
        </p>
        {images.length > 0 ? (
          <span className="inline-flex h-7 items-center gap-xs rounded-full bg-states-successHighlight px-sm font-body text-body-2 font-semibold text-states-online">
            <span className="size-2 rounded-full bg-states-success" />
            Published
          </span>
        ) : null}
      </div>
      {images.length === 0 ? (
        <p className="flex items-center gap-md font-body text-body-1 text-text-primary">
          <span className="size-2 shrink-0 rounded-xs bg-text-placeholder" />
          No published story yet
        </p>
      ) : (
        <div className="flex flex-col gap-md">
          <div className="flex gap-lg">
            {images.map((image) => (
              <button
                key={image.key}
                type="button"
                className="h-[249px] w-[140px] overflow-hidden rounded-xl border border-surfaces-z5"
                onClick={() => onOpen(image)}
              >
                <PublishedImage image={image} className="size-full object-cover" />
              </button>
            ))}
          </div>
          <p className="font-body text-body-1 text-text-secondary">
            Tap a story to see it full size.
          </p>
        </div>
      )}
    </section>
  );
}

function PublishedImage({
  image,
  className,
}: {
  image: MentorStoryImage;
  className?: string;
}) {
  const signed = useSignedMedia(image.url);
  if (!signed.url) return <div className={className} />;
  return <img src={signed.url} alt="" className={className} />;
}

function StoryEditorCard({
  review,
  onEdit,
}: {
  review: StoryReview | null;
  onEdit: () => void;
}) {
  const status = editorStatus(review);
  return (
    <section className="rounded-2xl border border-surfaces-z5 bg-surfaces-z0 px-xl py-xl">
      <div className="flex items-center gap-md">
        <p className="font-mono text-caption-3 uppercase text-text-tertiary">
          STORY EDITOR
        </p>
        {status.pill ? (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-sm py-xs font-body text-[13px] font-semibold leading-5 tracking-[0.2px]",
              status.pill.className,
            )}
          >
            {status.pill.label}
          </span>
        ) : null}
      </div>
      {status.note ? (
        <p className="mt-lg font-body text-body-1 text-text-secondary">
          {status.note}
        </p>
      ) : null}
      <Button
        size="lg"
        fullWidth={false}
        className="mt-lg"
        disabled={status.locked}
        leadingIcon={
          status.locked ? (
            <Clock className="size-6" />
          ) : status.create ? (
            <Plus className="size-6" />
          ) : (
            <Pencil className="size-6" />
          )
        }
        onPress={onEdit}
      >
        {status.button}
      </Button>
    </section>
  );
}

function editorStatus(review: StoryReview | null): {
  button: string;
  create: boolean;
  locked: boolean;
  note?: string;
  pill?: { label: string; className: string };
} {
  const status: StoryReviewStatus | "empty" = review?.status ?? "empty";
  switch (status) {
    case "empty":
      return { button: "Create story", create: true, locked: false };
    case "in_progress":
      return {
        button: "Edit story",
        create: false,
        locked: false,
        pill: { className: "bg-[#ffceb9] text-[#a03505]", label: "Draft" },
      };
    case "in_review":
      return {
        button: "In review",
        create: false,
        locked: true,
        note: "Sent to your lead mentor. You'll hear back once it's approved.",
        pill: { className: "bg-[#ffceb9] text-[#a03505]", label: "In review" },
      };
    case "changes_requested":
      return {
        button: "Edit story",
        create: false,
        locked: false,
        pill: {
          className: "bg-states-errorHighlight text-states-error",
          label: "Comments to address",
        },
      };
    case "approved":
      return {
        button: "Edit story",
        create: false,
        locked: false,
        note: "Editing creates a new version for review.",
      };
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}
